import React, { useMemo } from 'react';
import { Box, Typography, Tooltip, Avatar, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { colors } from '../design-system';

const EVENT_ICONS = {
  gol: { icon: '⚽', tone: 'success' },
  sustitucion: { icon: '🔄', tone: 'primary' },
  tarjeta: { icon: '🟨', tone: 'warning' },
  tarjeta_roja: { icon: '🟥', tone: 'error' },
  tarjeta_doble: { icon: '🟨🟥', tone: 'error' },
};

/**
 * Groups events by minute and team side, then assigns position (above/below).
 * Events at the same minute from the same team are merged into one group.
 * Assigns _level (0, 1, 2...) to stagger groups that are too close on the same side.
 */
const MIN_GAP = 4; // minimum minute gap before staggering
const groupEvents = (eventos) => {
  const sorted = [...eventos].sort((a, b) => (a.minuto || 0) - (b.minuto || 0));
  const groups = [];
  for (const evt of sorted) {
    const key = `${evt.minuto}_${evt.local}`;
    const last = groups[groups.length - 1];
    if (last && last._key === key) {
      last.events.push(evt);
    } else {
      groups.push({ _key: key, minuto: evt.minuto, local: evt.local, _above: evt.local === true, events: [evt] });
    }
  }
  // Assign _level to stagger overlapping groups on same side
  const aboveGroups = groups.filter(g => g._above);
  const belowGroups = groups.filter(g => !g._above);
  const assignLevels = (sideGroups) => {
    for (let i = 0; i < sideGroups.length; i++) {
      sideGroups[i]._level = 0;
      for (let j = i - 1; j >= 0; j--) {
        if (Math.abs(sideGroups[i].minuto - sideGroups[j].minuto) < MIN_GAP) {
          sideGroups[i]._level = Math.max(sideGroups[i]._level, sideGroups[j]._level + 1);
        } else break;
      }
    }
  };
  assignLevels(aboveGroups);
  assignLevels(belowGroups);
  return groups;
};

const MatchTimeline = ({
  eventos = [],
  equipoLocal,
  equipoVisitante,
  escudoLocal,
  escudoVisitante,
  duracionTotal = 90,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Use duracionTotal as the definitive match length
  const dur = Number(duracionTotal) || 90;
  const halfTime = Math.round(dur / 2);

  const maxMinute = dur;

  const grouped = useMemo(() => groupEvents(eventos), [eventos]);

  // Events beyond match duration are clamped to the end
  const getPosition = (minuto) => Math.min((Math.min(minuto, dur) / maxMinute) * 100, 98);
  const halfTimePos = getPosition(halfTime);

  const getTooltip = (evt) => {
    if (evt.tipo === 'gol')
      return `⚽ Gol de ${evt.jugador} (${evt.minuto}')`;
    if (evt.tipo === 'sustitucion')
      return `🔄 ${evt.jugadorEntra} ↔ ${evt.jugadorSale} (${evt.minuto}')`;
    if (evt.tipo === 'tarjeta') {
      const label = evt.esSegundaAmarilla ? '2ª Amarilla' : evt.tipoTarjeta === 101 ? 'Roja' : 'Amarilla';
      return `${evt.esSegundaAmarilla ? '🟨🟥' : evt.tipoTarjeta === 101 ? '🟥' : '🟨'} ${label} - ${evt.jugador} (${evt.minuto}')`;
    }    return `${evt.minuto}'`;
  };

  const getEventStyle = (evt) => {
    if (evt.tipo === 'tarjeta') {
      if (evt.esSegundaAmarilla) return EVENT_ICONS.tarjeta_doble;
      if (evt.tipoTarjeta === 101) return EVENT_ICONS.tarjeta_roja;
      return EVENT_ICONS.tarjeta;
    }
    return EVENT_ICONS[evt.tipo] || EVENT_ICONS.gol;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Team headers with color indicators */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, px: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {escudoLocal && <Avatar src={escudoLocal} sx={{ width: 20, height: 20 }} />}
          <Typography variant="caption" fontWeight={700} sx={{ color: isDark ? colors.primary[300] : colors.primary[700] }}>
            {equipoLocal}
          </Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.primary[500] }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>▲</Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Cronología
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>▼</Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.error[500] }} />
          <Typography variant="caption" fontWeight={700} sx={{ color: isDark ? colors.error[300] : colors.error[700] }}>
            {equipoVisitante}
          </Typography>
          {escudoVisitante && <Avatar src={escudoVisitante} sx={{ width: 20, height: 20 }} />}
        </Box>
      </Box>

      {/* Timeline bar */}
      <Box sx={{ position: 'relative', height: 90, mx: 0.5 }}>
        {/* Main bar */}
        <Box sx={{
          position: 'absolute', top: '50%', left: 0, right: 0,
          height: 4, borderRadius: 2, transform: 'translateY(-50%)',
          background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, isDark ? 0.52 : 0.32)} 0%, ${alpha(theme.palette.primary.main, isDark ? 0.72 : 0.52)} ${halfTimePos}%, ${theme.palette.divider} ${halfTimePos}%, ${alpha(theme.palette.primary.main, isDark ? 0.72 : 0.52)} ${halfTimePos + 1}%, ${alpha(theme.palette.primary.main, isDark ? 0.52 : 0.32)} 100%)`,
        }} />

        {/* Kick-off marker */}
        <Box sx={{
          position: 'absolute', top: '50%', left: 0, transform: 'translate(-50%, -50%)',
          width: 10, height: 10, borderRadius: '50%',
          backgroundColor: theme.palette.primary.main,
          border: `2px solid ${alpha(theme.palette.primary.main, isDark ? 0.42 : 0.28)}`,
          zIndex: 3,
        }} />

        {/* Half-time marker */}
        <Box sx={{
          position: 'absolute', top: '50%', left: `${halfTimePos}%`,
          transform: 'translate(-50%, -50%)',
          zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center',
        }}>
          <Box sx={{
            position: 'absolute', top: -22,
            backgroundColor: isDark ? colors.neutral[200] : colors.neutral[700],
            color: isDark ? colors.neutral[900] : '#fff',
            fontSize: '0.65rem', fontWeight: 700, px: 0.75, py: 0.2,
            borderRadius: 1, whiteSpace: 'nowrap',
          }}>
            Descanso
          </Box>
          <Box sx={{
            width: 2, height: 28, backgroundColor: theme.palette.text.secondary,
            position: 'absolute', top: -8,
          }} />
          <Box sx={{
            width: 12, height: 12, borderRadius: '50%',
            backgroundColor: theme.palette.text.secondary, border: `2px solid ${theme.palette.divider}`,
          }} />
        </Box>

        {/* Full-time marker */}
        <Box sx={{
          position: 'absolute', top: '50%', right: 0, transform: 'translate(50%, -50%)',
          zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center',
        }}>
          <Box sx={{
            position: 'absolute', top: -22,
            backgroundColor: isDark ? colors.neutral[200] : colors.neutral[700],
            color: isDark ? colors.neutral[900] : '#fff',
            fontSize: '0.65rem', fontWeight: 700, px: 0.75, py: 0.2,
            borderRadius: 1, whiteSpace: 'nowrap',
          }}>
            Final
          </Box>
          <Box sx={{
            width: 2, height: 28, backgroundColor: theme.palette.text.secondary,
            position: 'absolute', top: -8,
          }} />
          <Box sx={{
            width: 12, height: 12, borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            border: `2px solid ${alpha(theme.palette.primary.main, isDark ? 0.5 : 0.35)}`,
          }} />
        </Box>

        {/* Event markers — grouped by minute+team */}
        {grouped.map((group, idx) => {
          const pos = getPosition(group.minuto);
          const above = group._above;
          const level = group._level || 0;
          const levelOffset = level * 22; // px offset per stagger level
          const tooltipText = group.events.map(e => getTooltip(e)).join('\n');
          const primaryStyle = getEventStyle(group.events[0]);
          const eventColor = theme.palette[primaryStyle.tone]?.main || theme.palette.primary.main;
          const teamColor = group.local ? theme.palette.primary.main : theme.palette.error.main;

          return (
            <Tooltip key={idx} title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span>} arrow placement={above ? 'top' : 'bottom'}>
              <Box sx={{
                position: 'absolute',
                left: `${pos}%`,
                top: above ? `calc(50% - 8px - ${levelOffset + 22}px)` : `calc(50% + 8px + ${levelOffset}px)`,
                transform: above ? 'translateX(-70%)' : 'translateX(-30%)',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                cursor: 'pointer', zIndex: 4 + level,
              }}>
                {/* Event badge: icons + minute */}
                <Box data-testid="timeline-event-badge" sx={{
                  display: 'flex', alignItems: 'center', gap: '3px',
                  backgroundColor: alpha(teamColor, isDark ? 0.2 : 0.09),
                  border: `1.5px solid ${alpha(teamColor, isDark ? 0.72 : 0.58)}`,
                  borderLeft: `4px solid ${teamColor}`,
                  borderRadius: 10,
                  px: 1, py: 0.4,
                  transition: 'transform 0.15s',
                  '&:hover': { transform: 'scale(1.15)' },
                  whiteSpace: 'nowrap',
                  boxShadow: isDark
                    ? '0 2px 8px rgba(0, 0, 0, 0.35)'
                    : `0 2px 6px ${alpha(teamColor, 0.28)}`,
                }}>
                  {group.events.map((evt, i) => (
                    <Box key={i} sx={{ fontSize: '0.85rem', lineHeight: 1 }}>{getEventStyle(evt).icon}</Box>
                  ))}
                  <Typography sx={{
                    fontSize: '0.8rem', fontWeight: 900, lineHeight: 1,
                    color: isDark ? alpha(eventColor, 0.95) : eventColor,
                  }}>
                    {group.minuto}'
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Minute markers */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600 }}>0'</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600 }}>{halfTime}'</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600 }}>{dur}'</Typography>
      </Box>
    </Box>
  );
};

export default MatchTimeline;
